import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { Heart, ShieldCheck, Lightbulb, Users2 } from 'lucide-react'

const iconMap = {
  Heart,
  ShieldCheck,
  Lightbulb,
  Users2,
}

interface Value {
  icon: keyof typeof iconMap
  titleKey: string
  descKey: string
}

export function ValuesSection() {
  const { t } = useTranslation('pages')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const values: Value[] = [
    {
      icon: 'Heart',
      titleKey: 'chiSiamo.values.customerTitle',
      descKey: 'chiSiamo.values.customerDesc',
    },
    {
      icon: 'ShieldCheck',
      titleKey: 'chiSiamo.values.qualityTitle',
      descKey: 'chiSiamo.values.qualityDesc',
    },
    {
      icon: 'Lightbulb',
      titleKey: 'chiSiamo.values.innovationTitle',
      descKey: 'chiSiamo.values.innovationDesc',
    },
    {
      icon: 'Users2',
      titleKey: 'chiSiamo.values.collaborationTitle',
      descKey: 'chiSiamo.values.collaborationDesc',
    },
  ]

  return (
    <section id="values" ref={ref} className="py-24 bg-white">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            {t('chiSiamo.values.title')}
          </h2>
          <div className="w-20 h-1 bg-[#f75e47] mx-auto mb-8" />
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('chiSiamo.values.subtitle')}
          </p>
        </motion.div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => {
            const Icon = iconMap[value.icon]
            return (
              <motion.div
                key={value.titleKey}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
                className="group text-center"
              >
                <div className="relative w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#f75e47] to-[#f75e47]/80 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Icon className="text-white" size={36} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {t(value.titleKey)}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t(value.descKey)}
                </p>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
